const supportsSessionStorage = () => {
	try {
		return "sessionStorage" in globalThis && globalThis.sessionStorage !== null;
	} catch {
		return false;
	}
};

class Cache {
	storage;

	constructor() {
		this.storage = supportsSessionStorage()
			? sessionStorage
			: {
					getItem: () => {
						return "";
					},
					setItem: () => {
						return "";
					},
					length: 0,
					clear() {},
					key() {
						return "";
					},
					removeItem() {},
				};
	}

	exists(key) {
		return this.storage.getItem(key) !== null;
	}

	/**
	 * @param {String} key
	 * @param {String} value
	 */
	set(key, value) {
		this.storage.setItem(key, value);
	}

	/**
	 * @param {String} key
	 */
	get(key) {
		return this.storage.getItem(key);
	}

	delete(key) {
		this.storage.removeItem(key);
	}
}

export class Product {
	cache;

	constructor() {
		this.cache = new Cache();
	}

	/**
	 * @param {String} url
	 * @returns {Promise<ExtendedProduct | APIProduct>}
	 * @throws {Error}
	 */
	request = async (url) => {
		try {
			// Check cache first
			const cached = this.cache.get(url);
			if (cached) {
				return JSON.parse(cached);
			}

			// Fetch if not in cache
			const response = await fetch(url);

			if (response && !response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			if (!response) {
				return console.error("No response from server");
			}

			const data = await response?.json();

			// Try to cache the result
			try {
				this.cache.set(url, JSON.stringify(data));
			} catch (cacheError) {
				console.warn("Failed to cache data:", cacheError);
			}

			return data;
		} catch (error) {
			console.error("Error in request:", error);

			// Log to error tracking services
			if (globalThis.Bugsnag) globalThis.Bugsnag.notify(error);
			if (globalThis.Sentry) globalThis.Sentry.captureException(error);

			throw error; // Re-throw the error for the caller to handle
		}
	};

	/**
	 * @param {String} handle
	 * @returns {Promise<ExtendedProduct | null>}
	 * @throws {Error}
	 */
	loadExtendProduct = async (handle) => {
		const url = `${globalThis.Shopify.routes.root}products/${handle}?view=data`;
		return await this.request(url);
	};

	/**
	 * @param {String} handle
	 * @returns {Promise<APIProduct | null>}
	 * @throws {Error}
	 */
	loadProduct = async (handle) => {
		const url = `${globalThis.Shopify.routes.root}products/${handle}.js`;
		return await this.request(url);
	};

	/**
	 * @param {String} handle
	 * @param {RequestPriority} priority
	 * @returns {Promise<Element>}
	 */
	loadCard = async (handle, priority = "auto") => {
		const url = `${globalThis.Shopify.routes.root}products/${handle}?view=card`;

		try {
			// Check cache first
			const cached = this.cache.get(url);
			if (cached) {
				return document.createRange().createContextualFragment(cached).firstElementChild;
			}

			// Fetch if not in cache
			const response = await fetch(url, { priority });

			if (response && !response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			if (!response) {
				return console.error("No response from server");
			}

			const template = await response?.text();

			// Try to cache the result
			try {
				this.cache.set(url, template);
			} catch (cacheError) {
				console.warn("Failed to cache template:", cacheError);
			}

			return document.createRange().createContextualFragment(template).firstElementChild;
		} catch (error) {
			console.error("Error loading card:", error);

			// Log to error tracking services
			if (globalThis.Bugsnag) globalThis.Bugsnag.notify(error);
			if (globalThis.Sentry) globalThis.Sentry.captureException(error);

			throw error; // Re-throw to allow calling code to handle the error
		}
	};
}

export const product = new Product();

globalThis.theme.product = product;
